import { Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { CreativeCredit, SaveCreativeCreditInput } from 'sr-types/lib/person/v1/graphql';
import { UserContext } from '../../../common/auth/UserContext';
import Confirm from '../../../common/components/Confirm';
import {
    createCreativeCreditInputObject,
    creativeCreditToInput,
    useRemoveCreativeCreditFromPerson,
    useUpdatePersonCreativeCredit
} from '../../../common/components/person/person';
import I18n, { useI18n } from '../../../common/i18n/I18n';
import { PROFILES } from '../../../common/nav/apps';
import { failedToastMessage, successToastMessage } from '../../../common/utils/commonUtils';
import useHistory from '../../../common/utils/useHistory';
import { buildCreativeCreditRows, WorkEntriesDatagrid } from '../../../common/workEntries';
import { useProductionDetailsData } from '../../../production/helpers/productionApi';
import { EMPTY_CREATIVE_CREDIT, getFullName } from '../../person';
import CreativeCreditEditor from '../editComponents/CreativeCreditEditor';
import SideCard from '../sideCard/SideCard';

export default function CreativeCreditsCard({ creativeCredits, allowedEdit, personName, fromMarketplace = false }) {
    const { application, userProfile } = useContext(UserContext);
    const { routeParams } = useHistory();
    const { entityId } = routeParams;
    const t = useI18n();

    const { updatePersonCreativeCredit, isCreativeCreditSaving } = useUpdatePersonCreativeCredit();
    const { removeCreativeCreditFromPerson, isRemoving: isRemovingCreativeCredit } =
        useRemoveCreativeCreditFromPerson();

    const id = useMemo(
        () => (application.id === PROFILES.id && !entityId ? userProfile.personReference.id : entityId),
        [application.id, entityId, userProfile.personReference.id]
    );

    const personFullName = getFullName(personName);
    const keyCreditTooltip = t('person.creative.credits.key.credit');
    const keyCreditQuickFilterTooltip = t('credits.filter.key.credit');

    const [createState, setCreateState] = useState(EMPTY_CREATIVE_CREDIT);
    const [existingState, setExistingState] = useState(undefined);
    const [addCreativeCreditsModalOpen, setIsAddCreativeCreditsModalOpen] = useState(false);
    const [isCreativeCreditsModalOpen, setIsCreativeModalOpen] = useState<boolean>(false);
    const [isDeleteCreditConfirmOpen, setIsDeleteCreditConfirmOpen] = useState(false);
    const [creditPendingDelete, setCreditPendingDelete] = useState<CreativeCredit | null>(null);

    const handleEditCredit = useCallback(
        (credit: CreativeCredit) => {
            if (!allowedEdit || !id) return;
            const input = creativeCreditToInput(
                cloneDeep({
                    ...credit,
                    dateRange: credit.dateRange ?? { start: '', end: '' }
                })
            );
            setExistingState(cloneDeep(input));
            setCreateState(input);
            setIsAddCreativeCreditsModalOpen(true);
        },
        [allowedEdit, id]
    );

    const handleRequestDeleteCredit = useCallback(
        (credit: CreativeCredit) => {
            if (!allowedEdit || !id) return;
            setCreditPendingDelete(credit);
            setIsDeleteCreditConfirmOpen(true);
        },
        [allowedEdit, id]
    );

    const handleConfirmDeleteCredit = useCallback(() => {
        if (!creditPendingDelete || !id) return;
        removeCreativeCreditFromPerson({ id, input: creativeCreditToInput(creditPendingDelete) }).finally(() => {
            setIsDeleteCreditConfirmOpen(false);
            setCreditPendingDelete(null);
        });
    }, [creditPendingDelete, id, removeCreativeCreditFromPerson]);

    const { productionDetailsData, productionDetailsLoading } = useProductionDetailsData(
        createState.showReference?.id,
        !createState.showReference
    );

    useEffect(() => {
        const productionType = productionDetailsData?.production?.productionType;
        if (productionType) {
            setCreateState((prev) => ({ ...prev, showType: productionType }));
        }
    }, [productionDetailsData]);

    const onClose = () => {
        setIsAddCreativeCreditsModalOpen(false);
        setCreateState(EMPTY_CREATIVE_CREDIT);
        setExistingState(undefined);
    };

    const onCreateCreativeCredit = () => {
        const input: SaveCreativeCreditInput = {
            new: createCreativeCreditInputObject(createState),
            existing: existingState || {},
            forceUserChange: true
        };
        updatePersonCreativeCredit({ id, input })
            .then(() => successToastMessage('person.edit.success', { person: personFullName }))
            .catch(() => failedToastMessage('person.edit.error'))
            .finally(onClose);
    };

    // Render the grid only when there are credits; SideCard renders its own NotFound otherwise.
    const creditsList = useMemo(() => {
        const credits = creativeCredits ?? [];
        if (credits.length === 0) return null;
        const rowActionsOn = Boolean(allowedEdit && id);
        const inlineAddButton = allowedEdit && id ? { onAdd: () => setIsAddCreativeCreditsModalOpen(true) } : undefined;
        const rows = buildCreativeCreditRows(credits);

        return (
            <WorkEntriesDatagrid
                rows={rows}
                enableSelection={false}
                enableKeyQuickFilter
                enableReorder={false}
                rowActionsEnabled={rowActionsOn}
                showBackButton={false}
                compactHorizontalPadding={fromMarketplace}
                inlineAddButton={inlineAddButton}
                keyEntryTooltip={keyCreditTooltip}
                keyQuickFilterTooltip={keyCreditQuickFilterTooltip}
                onEdit={(row) => handleEditCredit(row.source)}
                onRequestDelete={(row) => handleRequestDeleteCredit(row.source)}
            />
        );
    }, [
        creativeCredits,
        allowedEdit,
        id,
        fromMarketplace,
        keyCreditTooltip,
        keyCreditQuickFilterTooltip,
        handleEditCredit,
        handleRequestDeleteCredit
    ]);

    return (
        <>
            <SideCard
                id={'creative-credits'}
                title={<I18n token="person.creative.credits.title" />}
                modalTitle={<I18n token="modal.creative.credits.title" />}
                isModalOpen={isCreativeCreditsModalOpen}
                setIsModalOpen={setIsCreativeModalOpen}
                setIsCreateModalOpen={setIsAddCreativeCreditsModalOpen}
                allowedEdit={allowedEdit}
                fromMarketplace={fromMarketplace}
                fullWidthContent
                suppressAddButton={Boolean(allowedEdit && id)}
            >
                {creditsList ? [creditsList] : []}
            </SideCard>

            {addCreativeCreditsModalOpen && (
                <CreativeCreditEditor
                    createState={createState}
                    setCreateState={setCreateState}
                    isAddCreativeCreditsModalOpen={addCreativeCreditsModalOpen}
                    onClose={onClose}
                    onSave={onCreateCreativeCredit}
                    isCreativeCreditSaving={isCreativeCreditSaving}
                    title={<I18n token={`person.creative.credits.${existingState ? 'edit' : 'add'}.title`} />}
                    disableTypeField={productionDetailsLoading || !!productionDetailsData?.production?.productionType}
                />
            )}

            <Confirm
                open={isDeleteCreditConfirmOpen}
                title={<I18n token="person.form.confirm.delete.title" />}
                onConfirm={handleConfirmDeleteCredit}
                body={
                    <Typography>
                        <I18n token="person.form.confirm.delete.message" />
                    </Typography>
                }
                onCancel={() => {
                    setIsDeleteCreditConfirmOpen(false);
                    setCreditPendingDelete(null);
                }}
                isLoading={isRemovingCreativeCredit}
            />
        </>
    );
}
